import { type DataTableColumn, DataTableV2 } from "@/components/ui/data-table-v2";
import { EmptyStateV2 } from "@/components/ui/empty-state-v2";
import { ErrorState } from "@/components/ui/error-state";
import { KpiReadout } from "@/components/ui/kpi-readout";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { StatusBadge } from "@/components/ui/status-badge";
import { useCurrentProject } from "@/features/projects/current-project";
import { formatBytes } from "@/lib/format";
import { getStatusSemantic } from "@/lib/status";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Database, Upload } from "lucide-react";
import { useState } from "react";
import { type DatasetListItem, useDatasets } from "./queries";
import { UploadDatasetDialog } from "./upload-dialog";

const columns: (ColumnDef<DatasetListItem, unknown> & DataTableColumn)[] = [
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  { header: "Kind", accessorKey: "kind", mono: true },
  {
    header: "Rows",
    accessorKey: "rows",
    mono: true,
    cell: ({ row }) => <span>{row.original.rows?.toLocaleString() ?? "—"}</span>,
  },
  {
    header: "Cols",
    accessorKey: "cols",
    mono: true,
    cell: ({ row }) => <span>{row.original.cols ?? "—"}</span>,
  },
  {
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    header: "Created",
    accessorKey: "created_at",
    mono: true,
    cell: ({ row }) => {
      const d = new Date(row.original.created_at);
      return <span>{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>;
    },
  },
];

export function DatasetsListPage() {
  const navigate = useNavigate();
  const { projectId, projectName } = useCurrentProject();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading, isError, refetch } = useDatasets(projectId, { page, pageSize });
  const [uploadOpen, setUploadOpen] = useState(false);

  if (!projectId) {
    return (
      <div className="pt-2">
        <EmptyStateV2
          icon={<Database size={32} />}
          title="No project selected"
          description="Choose a project to view its datasets."
        />
      </div>
    );
  }

  const items = data?.items ?? [];
  const totalRows = items.reduce((s, d) => s + (d.rows ?? 0), 0);
  const totalBytes = items.reduce((s, d) => s + (d.size_bytes ?? 0), 0);
  const readyCount = items.filter((d) => d.status === "ready").length;

  return (
    <>
      <PageHeaderV2
        eyebrow="Data"
        title="Datasets"
        meta={
          <span>
            PROJECT{" "}
            <strong className="font-semibold text-navy-700 dark:text-zinc-100">
              {projectName}
            </strong>
            {" · "}
            <strong className="font-semibold text-navy-700 dark:text-zinc-100">
              {data?.total ?? 0}
            </strong>{" "}
            datasets
            {" · "}
            <strong className="font-semibold text-navy-700 dark:text-zinc-100">
              {totalRows >= 1_000_000
                ? `${(totalRows / 1_000_000).toFixed(2)}M`
                : totalRows.toLocaleString()}
            </strong>{" "}
            rows indexed
          </span>
        }
        action={
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="flex items-center gap-2 rounded-[10px] bg-navy-700 px-4 py-2.5 text-[12.5px] font-semibold text-white shadow-[0_6px_16px_rgba(11,20,38,0.16)] transition-colors hover:bg-navy-500 dark:bg-teal-500 dark:text-navy-900 dark:hover:bg-teal-300"
          >
            <Upload size={14} />
            Upload CSV / Parquet
          </button>
        }
      />

      {/* KPI strip */}
      <div className="mb-5 grid grid-cols-4 gap-px overflow-hidden rounded-[14px] border border-line bg-line">
        <KpiReadout label="Datasets" value={String(data?.total ?? 0)} />
        <KpiReadout
          label="Rows indexed"
          value={
            totalRows >= 1_000_000 ? (totalRows / 1_000_000).toFixed(2) : totalRows.toLocaleString()
          }
          suffix={totalRows >= 1_000_000 ? "M" : undefined}
        />
        <KpiReadout label="Storage" value={formatBytes(totalBytes)} />
        <KpiReadout
          label="Ready to train"
          value={String(readyCount)}
          suffix={`/${data?.total ?? 0}`}
          accent
          sparkline={items.slice(-7).map((d) => (d.status === "ready" ? 80 : 30))}
        />
      </div>

      {/* Table */}
      <div className="mb-7">
        {isLoading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : isError ? (
          <ErrorState message="Failed to load datasets" onRetry={() => refetch()} />
        ) : items.length === 0 ? (
          <EmptyStateV2
            icon={<Database size={32} />}
            title="No datasets yet"
            description="Upload a CSV or Parquet file to get started."
            action={
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="rounded-md bg-navy-700 px-3 py-1.5 text-xs font-medium text-white dark:bg-teal-500 dark:text-navy-900"
              >
                Upload
              </button>
            }
          />
        ) : (
          <DataTableV2
            data={items}
            columns={columns}
            caption="All datasets"
            getRowAccent={(d) => getStatusSemantic(d.status)}
            onRowClick={(d) =>
              navigate({ to: "/datasets/$datasetId", params: { datasetId: d.id } })
            }
            page={page}
            pageSize={pageSize}
            total={data?.total ?? items.length}
            onPageChange={setPage}
          />
        )}
      </div>

      <UploadDatasetDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  );
}
